import { useState, useEffect } from 'react';
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Globe } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Common timezone options for medical practices
const TIMEZONE_OPTIONS = [
  // South Africa
  { value: "Africa/Johannesburg", label: "South Africa (SAST)", region: "Africa", offset: "+02:00" },
  { value: "Africa/Cape_Town", label: "Cape Town (SAST)", region: "Africa", offset: "+02:00" },
  
  // Major African cities
  { value: "Africa/Cairo", label: "Cairo (EET)", region: "Africa", offset: "+02:00" },
  { value: "Africa/Lagos", label: "Lagos (WAT)", region: "Africa", offset: "+01:00" },
  { value: "Africa/Nairobi", label: "Nairobi (EAT)", region: "Africa", offset: "+03:00" },
  { value: "Africa/Casablanca", label: "Casablanca (WET)", region: "Africa", offset: "+00:00" },
  
  // Europe
  { value: "Europe/London", label: "London (GMT/BST)", region: "Europe", offset: "+00:00/+01:00" },
  { value: "Europe/Berlin", label: "Berlin (CET/CEST)", region: "Europe", offset: "+01:00/+02:00" },
  { value: "Europe/Paris", label: "Paris (CET/CEST)", region: "Europe", offset: "+01:00/+02:00" },
  { value: "Europe/Rome", label: "Rome (CET/CEST)", region: "Europe", offset: "+01:00/+02:00" },
  
  // Americas
  { value: "America/New_York", label: "New York (EST/EDT)", region: "Americas", offset: "-05:00/-04:00" },
  { value: "America/Chicago", label: "Chicago (CST/CDT)", region: "Americas", offset: "-06:00/-05:00" },
  { value: "America/Denver", label: "Denver (MST/MDT)", region: "Americas", offset: "-07:00/-06:00" },
  { value: "America/Los_Angeles", label: "Los Angeles (PST/PDT)", region: "Americas", offset: "-08:00/-07:00" },
  { value: "America/Toronto", label: "Toronto (EST/EDT)", region: "Americas", offset: "-05:00/-04:00" },
  
  // Asia
  { value: "Asia/Dubai", label: "Dubai (GST)", region: "Asia", offset: "+04:00" },
  { value: "Asia/Singapore", label: "Singapore (SGT)", region: "Asia", offset: "+08:00" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)", region: "Asia", offset: "+09:00" },
  { value: "Asia/Shanghai", label: "Shanghai (CST)", region: "Asia", offset: "+08:00" },
  { value: "Asia/Kolkata", label: "Mumbai/Delhi (IST)", region: "Asia", offset: "+05:30" },
  
  // Australia/Pacific
  { value: "Australia/Sydney", label: "Sydney (AEST/AEDT)", region: "Australia", offset: "+10:00/+11:00" },
  { value: "Australia/Melbourne", label: "Melbourne (AEST/AEDT)", region: "Australia", offset: "+10:00/+11:00" },
  { value: "Pacific/Auckland", label: "Auckland (NZST/NZDT)", region: "Pacific", offset: "+12:00/+13:00" },
];

// Group timezones by region
const GROUPED_TIMEZONES = TIMEZONE_OPTIONS.reduce((groups, tz) => {
  const region = tz.region;
  if (!groups[region]) {
    groups[region] = [];
  }
  groups[region].push(tz);
  return groups;
}, {});

export default function TimezoneSelector({ 
  value = "", 
  onChange, 
  disabled = false,
  showCurrentTime = true,
  error = null 
}) {
  const [currentTime, setCurrentTime] = useState("");

  // Update current time when timezone changes
  const updateCurrentTime = (timezone) => {
    if (!timezone) return;
    
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
      
      setCurrentTime(formatter.format(now));
    } catch (error) {
      console.warn('Failed to format time for timezone:', timezone, error);
      setCurrentTime("");
    }
  };

  const handleTimezoneChange = (selectedTimezone) => {
    onChange(selectedTimezone);
    if (showCurrentTime) {
      updateCurrentTime(selectedTimezone);
    }
  };

  // Update current time when component mounts or value changes
  useEffect(() => {
    if (value && showCurrentTime) {
      updateCurrentTime(value);
      // Update time every second
      const interval = setInterval(() => updateCurrentTime(value), 1000);
      return () => clearInterval(interval);
    }
  }, [value, showCurrentTime]);

  const selectedOption = TIMEZONE_OPTIONS.find(tz => tz.value === value);

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Globe className="w-4 h-4" />
          Practice Timezone
        </Label>
        
        <Select value={value} onValueChange={handleTimezoneChange} disabled={disabled}>
          <SelectTrigger className={error ? "border-red-500" : ""}>
            <SelectValue placeholder="Select your practice timezone..." />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {Object.entries(GROUPED_TIMEZONES).map(([region, timezones]) => (
              <div key={region}>
                <div className="px-2 py-1.5 text-sm font-semibold text-slate-600 bg-slate-50">
                  {region}
                </div>
                {timezones.map((timezone) => (
                  <SelectItem key={timezone.value} value={timezone.value}>
                    <div className="flex items-center justify-between w-full">
                      <span>{timezone.label}</span>
                      <span className="text-xs text-slate-500 ml-2">
                        {timezone.offset}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {showCurrentTime && currentTime && selectedOption && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <Clock className="w-4 h-4 text-blue-600" />
          <div className="text-sm">
            <span className="font-medium text-blue-800">Current time in {selectedOption.label}: </span>
            <span className="font-mono text-blue-700">{currentTime}</span>
          </div>
        </div>
      )}

      {selectedOption && (
        <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded">
          <strong>Selected:</strong> {selectedOption.label} ({selectedOption.offset})
          <br />
          <strong>Note:</strong> All appointment times will be displayed in this timezone for your practice.
        </div>
      )}
    </div>
  );
}